import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaPhoneAlt, FaTimes } from "react-icons/fa";
import { useIncomingCall } from "../hooks/useIncomingCall";

// Mounted once in SpeakingPartnerLayout so it's visible on every page under
// /speaking-partner/* — without this, a ringing call is only visible to
// someone who happens to already have that exact chat open. Browse renders
// its own inline "calling card" instead (fits its layout better), so this
// sticky strip steps aside there to avoid showing the same call twice.
const IncomingCallBanner: React.FC = () => {
  const { incoming, dismiss } = useIncomingCall();
  const location = useLocation();
  const navigate = useNavigate();

  if (!incoming) return null;
  if (location.pathname.startsWith("/speaking-partner/browse")) return null;
  // Skip while already on this chat or its call page — that page shows its
  // own inline "started a call" banner, no need to double up.
  if (location.pathname.startsWith(`/speaking-partner/chats/${incoming.chatId}`)) return null;

  return (
    // Sticky, not fixed — stays in normal document flow so it pushes the page's
    // own header down instead of floating over/colliding with it, but still
    // stays pinned to the top of the viewport while scrolling.
    <div className="sticky top-0 z-50 w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md">
      <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <FaPhoneAlt className="animate-pulse flex-shrink-0" size={14} />
        <span className="text-sm font-extrabold flex-1 min-w-0 truncate">
          {incoming.displayName} {incoming.isGroup ? "added you to a call" : "is calling"}
        </span>
        <button
          onClick={() => navigate(incoming.joinPath)}
          className="flex-shrink-0 text-xs font-extrabold px-3.5 py-1.5 rounded-lg bg-white text-emerald-700 hover:bg-white/90 transition-colors"
        >
          Join
        </button>
        <button onClick={dismiss} title="Dismiss" className="flex-shrink-0 p-1 text-white/70 hover:text-white">
          <FaTimes size={14} />
        </button>
      </div>
    </div>
  );
};

export default IncomingCallBanner;
